// src/features/staff/pages/StaffDetailsPage/components/StaffFilesTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Alert } from '@mui/material';
import { AttachFile as AttachFileIcon, Folder as FolderIcon } from '@mui/icons-material';

import { useStaff } from '../../../hooks/useStaff';
import { useStaffFolder } from '../../../hooks/useStaffFolder';
import { usePermissions } from '../../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../../hooks/mui/useSnackBar';
import SelectFileDialog from '../../../../files/components/SelectFileDialog/SelectFileDialog';
import FileExplorer from '../../../../files/components/FileExplorer';
import FileSourceButtons from '../../../../files/components/FileSourceButtons';
import { downloadFileLink } from '../../../../files/hooks/useFileUrl';
import { useFiles } from '../../../../files/hooks/useFiles';
import { useFilePicker, useUploadQueue, UploadProgressList } from '../../../../files/upload';
import StaffTabSection from './StaffTabSection';

// Map StaffMemberFileDTO to the shape FileExplorer expects
const toFileEntry = (sf) => ({
  file_id: sf.file_id,
  filename: sf.filename,
  filetype: sf.filetype,
  file_size: sf.file_size,
  uploaded_at: sf.added_at,
  // Who attached it to the staff member
  uploader_id: sf.added_by,
  // Carry the link ID so the file can be detached
  staff_member_file_id: sf.staff_member_file_id,
});

/**
 * Contracts and other documents for a staff member. New uploads land in
 * Filer under Staff/<name>; removing one only detaches it. The page only shows
 * this tab with staff:update and file:read.
 */
export default function StaffFilesTab({ staffMember, canAdd = true }) {
  const { t } = useTranslation(['staff', 'common', 'files']);
  const permissions = usePermissions();
  const { showSuccess, showError } = useSnackbar();
  const staffId = staffMember.staff_member_id;

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [browseFolder, setBrowseFolder] = useState(null);

  const { fetchStaffFiles, attachStaffFile, removeStaffFile } = useStaff();
  const { uploadFile } = useFiles();
  const findStaffFolder = useStaffFolder();

  // Adding also needs write access to the staff folder in Filer
  const canUpload = canAdd && permissions.hasScope('file:upload');

  const loadFiles = useCallback(async () => {
    setError(null);
    const result = await fetchStaffFiles(staffId);
    if (result.success) {
      setFiles((result.data.files || []).map(toFileEntry));
    } else {
      setError(t('staff:files.load_failed'));
    }
    setLoading(false);
  }, [staffId, fetchStaffFiles, t]);

  useEffect(() => {
    setLoading(true);
    loadFiles();
  }, [loadFiles]);

  // New files go to the staff member's own folder (created on first upload)
  // and are attached straight away
  const uploads = useUploadQueue({
    upload: async (file, { onUploadProgress }) => {
      const folder = await findStaffFolder(staffMember.name, { create: true });
      return uploadFile(file, { folder_id: folder?.folderId ?? null, is_system_file: false, onUploadProgress });
    },
    onUploaded: async (result) => {
      const attached = await attachStaffFile(staffId, result.data.data.file_id);
      if (!attached.success) showError(t('staff:files.attach_failed'));
    },
    onFinished: (results) => { if (results.length) loadFiles(); },
  });
  const picker = useFilePicker({ onFiles: uploads.enqueue });

  // Picking files already in Filer: start in the staff member's folder if there is one
  const handleOpenDialog = useCallback(async () => {
    setBrowseFolder(await findStaffFolder(staffMember.name));
    setDialogOpen(true);
  }, [staffMember.name, findStaffFolder]);

  const handleAttachFiles = useCallback(async (selectedFileIds) => {
    setAttaching(true);
    let anyFailed = false;
    for (const fileId of selectedFileIds) {
      const result = await attachStaffFile(staffId, fileId);
      if (!result.success) anyFailed = true;
    }
    setDialogOpen(false);
    setAttaching(false);
    if (anyFailed) {
      showError(t('staff:files.attach_failed'));
    } else {
      showSuccess(t('staff:files.attached'));
    }
    await loadFiles();
  }, [staffId, attachStaffFile, loadFiles, showSuccess, showError, t]);

  // "Delete" from the preview and "remove" from the menu both just detach the
  // file; it stays in Filer
  const handleFileAction = useCallback(async (action, file) => {
    if (action === 'delete' || action === 'remove') {
      const result = await removeStaffFile(staffId, file.staff_member_file_id);
      if (result.success) {
        showSuccess(t('staff:files.removed'));
        await loadFiles();
      } else {
        showError(t('staff:files.remove_failed'));
      }
    } else if (action === 'download') {
      downloadFileLink(file.file_id, { filename: file.filename });
    }
  }, [staffId, removeStaffFile, loadFiles, showSuccess, showError, t]);

  const removeLabel = t('staff:files.remove');

  const emptyState = (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={4} color="text.secondary">
      <AttachFileIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="body1">{t('staff:files.no_files')}</Typography>
      {canUpload && <Typography variant="body2">{t('staff:files.no_files_hint')}</Typography>}
    </Box>
  );

  return (
    <>
      <StaffTabSection
        icon={FolderIcon}
        title={t('staff:files.title', { name: staffMember.name })}
        subtitle={!loading && !error ? t('staff:files.count', { count: files.length }) : null}
        loading={loading}
        actions={(
          <FileSourceButtons
            onChoose={canAdd ? handleOpenDialog : undefined}
            onUpload={canUpload ? picker.open : undefined}
            disabled={uploads.busy}
            size="medium"
          />
        )}
        fill
      >
        {picker.input}
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ flex: 1, minHeight: 300, overflow: 'hidden' }}>
            <FileExplorer
              files={files}
              folders={[]}
              layout="full"
              enableSearch={files.length > 5}
              enablePreview={true}
              enableViewToggle={true}
              onFileAction={handleFileAction}
              fileActions={canAdd ? ['download', { key: 'remove', label: removeLabel }] : ['download']}
              deleteLabel={removeLabel}
              selectionMode="single"
              emptyState={emptyState}
              onFilesDropped={canUpload ? uploads.enqueue : undefined}
              dropLabel={t('staff:files.drop_files', { name: staffMember.name })}
            />
          </Box>
        )}
      </StaffTabSection>

      <SelectFileDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelectFile={handleAttachFiles}
        multiSelect={true}
        excludedFileIds={files.map(f => f.file_id)}
        allowUpload={canUpload}
        title={t('staff:files.choose_existing')}
        loading={attaching}
        startingFolderId={browseFolder?.folderId ?? null}
        initialBreadcrumbs={browseFolder?.breadcrumbs ?? null}
      />

      <UploadProgressList
        variant="floating"
        items={uploads.items}
        onDismiss={uploads.dismiss}
        onClose={uploads.clearFinished}
      />
    </>
  );
}
